import React from 'react';
import { MicroExperienceManagerContext } from './Context';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableHead,
    TableRow,
    TextField,
    Button,
    Collapse,
    Grid,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Typography,
} from '@mui/material';
import { HighlightOff } from '@mui/icons-material';
import MicroContentEditPanel from './MicroContentEditPanel';
import TagPrinter from './TagPrinter';
import TagsFilter from 'Modules/Components/TagsFilter';
import { TagsContext } from 'Modules/Components/TagsContext';

export default function MicroContentList({ onSelectMicroContent }) {
    const context = React.useContext(MicroExperienceManagerContext);
    const [mcList, setMcList] = React.useState([]);
    const [filters, setFilters] = React.useState({});
    const [filteredList, setFilteredList] = React.useState([]);

    React.useEffect(() => {
        let results = [...mcList];
        if (filters.searchText) {
            const txt = filters.searchText.toLowerCase() || '';
            results = results.filter((mx) => mx.name?.toLowerCase().includes(txt));
        }
        if (filters.effortSize) {
            results = results.filter((mx) => filters.effortSize === mx.effortSize);
        }
        if (filters.effortValue) {
            results = results.filter((mx) => filters.effortValue === mx.effortValue);
        }

        if (filters.tags && filters.tags.length) {
            results = results.filter((mx) => {
                // the tags filter <Select> passes through and array of strings
                // in the form 'GroupName_tagid'
                // microexperience tags are an object keyd on group with an array of tag id integers
                if(mx.tags)
                return filters.tags.some((group_id) => {
                    if (!group_id) return null;
                    let [group, fid] = group_id.split('_');
                    return mx.tags[group]?.find((mxid) => mxid === parseInt(fid));
                });
                return null;
            });
        }

        setFilteredList(results);
    }, [filters, mcList]);

    React.useState(() => {
        setMcList(context.microContentList);
        setFilteredList(context.microContentList);
    }, []);

    function rowSelected(row) {
        console.log({ rowSelected: row });
    }

    function updateFilters(newFilter) {
        setFilters({ ...filters, ...newFilter });
        setPage(0);
    }

    function resetFilters() {
        setFilters({});
        setPage(0);
    }

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    return (
        <Stack direction="column">
            <FilterBar filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} flex={1} />
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <TableContainer component={Box} flex={1} sx={{ p: 0, overflow: 'auto', minHeight: 800, width: 1 }}>
                        <Table stickyHeader sx={{ flex: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Effort</TableCell>
                                    <TableCell>Tags</TableCell>
                                    <TableCell>Owner</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                    <MicroContentTableRow
                                        key={`mxtablerow-${row.id}`}
                                        row={row}
                                        onSelect={rowSelected}
                                        onSelectMicroContent={onSelectMicroContent}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10]}
                        component="div"
                        count={filteredList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Grid>
            </Grid>
        </Stack>
    );
}

function FilterBar({ updateFilters, resetFilters }) {
    const { tagList, tagGroups } = React.useContext(TagsContext);

    const [searchText, setSearchText] = React.useState('');
    const [effortSize, setEffortSize] = React.useState('');
    const [effortValue, setEffortValue] = React.useState('');
    const [selectedTags, setSelectedTags] = React.useState([]);

    function searchTextFilterChagned(val) {
        updateFilters({ searchText: val });
        setSearchText(val);
    }
    function effortSizeFilterChagned(val) {
        updateFilters({ effortSize: val });
        setEffortSize(val);
    }
    function effortValueFilterChagned(val) {
        updateFilters({ effortValue: val });
        setEffortValue(val);
    }
    function tagsFilterChanged(val) {
        updateFilters({ tags: val });
        setSelectedTags(val);
    }

    function resetFiltersClicked() {
        setSearchText('');
        setEffortSize('');
        setEffortValue('');
        setSelectedTags([]);
        resetFilters();
    }

    return (
        <Grid container spacing={1} sx={{ pb: 2, backgroundColor: 'amazingco.tableheader' }}>
            <Grid item xs={12}><Typography variant="h6">Select Experience Content</Typography></Grid>
            <Grid item xs={3}>
                <InputLabel id="mx-search-label">Search</InputLabel>
                <TextField
                    size="small"
                    fullWidth
                    labelId="mx-search-label"
                    value={searchText}
                    onChange={(e) => searchTextFilterChagned(e.target.value)}
                />
            </Grid>
            <Grid item xs={2}>
                <InputLabel id="mx-filter-effort-size-label">Effort Size</InputLabel>
                <Select
                    displayEmpty
                    size="small"
                    fullWidth
                    labelId="mx-filter-effort-size-label"
                    value={effortSize}
                    onChange={(e) => effortSizeFilterChagned(e.target.value)}
                >
                    <MenuItem value="">-- Select --</MenuItem>
                    <MenuItem value="XS">XS</MenuItem>
                    <MenuItem value="S">S</MenuItem>
                    <MenuItem value="M">M</MenuItem>
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="XL">XL</MenuItem>
                </Select>
            </Grid>
            <Grid item xs={2}>
                <InputLabel id="mx-filter-effort-value-label">Effort Value</InputLabel>
                <Select
                    displayEmpty
                    size="small"
                    fullWidth
                    labelId="mx-filter-effort-value-label"
                    value={effortValue}
                    onChange={(e) => effortValueFilterChagned(e.target.value)}
                >
                    <MenuItem value="">-- Select --</MenuItem>
                    <MenuItem value="TIMEFILLER">TimeFiller</MenuItem>
                    <MenuItem value="MILESTONE">Milestone</MenuItem>
                </Select>
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={2}>
                <TagsFilter onChange={tagsFilterChanged} value={selectedTags} tagGroups={tagGroups} tagList={tagList} />
            </Grid>
            <Grid item xs={1} display="flex" sx={{ justifyContent: 'center', pt: 1, mb: 1 }}>
                <IconButton onClick={resetFiltersClicked}>
                    <HighlightOff />
                </IconButton>
            </Grid>
        </Grid>
    );
}

function MicroContentTableRow({ row, onSelectMicroContent }) {
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment>
            <TableRow
                onClick={() => setOpen(!open)}
                style={{ cursor: 'pointer' }}
                key={row.id}
                sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    td: { verticalAlign: 'top', backgroundColor: open ? '#ddd' : 'reset' },
                }}
            >
                <TableCell>{row.name}</TableCell>
                <TableCell>
                    {row.effortSize} : {row.effortValue}
                </TableCell>
                <TableCell>
                    <TagPrinter tagIdList={row.tags} />
                </TableCell>
                <TableCell>{row.owner}</TableCell>
                <TableCell>
                    <Button
                        sx={{ mr: 1 }}
                        variant="contained"
                        size="small"
                        color="secondary"
                        onClick={() => setOpen(!open)}
                    >
                        Details
                    </Button>
                    {/* <Button variant='contained' size='small' color='primary' onClick={(e) => { e.stopPropagation(); setAssign(row) }}>Assign</Button> */}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={5} sx={{ p: open ? 3 : 0, backgroundColor: '#eee' }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <MicroContentEditPanel microContent={row} onSelectMicroContent={onSelectMicroContent} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}
